import { type PropsWithChildren, useState } from 'react';
import type { AutoCompleteOption } from '@/components/AutoComplete';
import type { BreadcrumbItem } from '@/components/Breadcrumb';
import { SearchAutoComplete } from '@/components/SearchAutoComplete';
import { StickyHeaderPage } from '@/components/StickyHeaderPage';
import type { BeneficiaryFormatted } from '../beneficiaries.types';
import { BeneficiaryProfile } from './BeneficiaryProfile';

type BeneficiaryPageProps<T> = PropsWithChildren<{
  pathLink: BreadcrumbItem[];
  searchBar?: {
    placeholder: string;
    options: AutoCompleteOption<T>[];
    onSelect: (value: T) => void;
    onSearchChange?: (search: string) => void;
    isLoading?: boolean;
  };
  beneficiary: {
    data: BeneficiaryFormatted;
    infoBlocks: {
      id: string;
      icon: React.ReactNode;
      text?: string;
    }[];
  };
  subHeader: React.ReactNode;
}>;

export function BeneficiaryPageBase<T>(props: BeneficiaryPageProps<T>) {
  const { pathLink, searchBar, beneficiary, children, subHeader } = props;
  const [search, setSearch] = useState('');

  return (
    <StickyHeaderPage
      contentClassName="overflow-y-auto"
      pathLink={pathLink}
      header={
        <BeneficiaryProfile beneficiary={beneficiary.data} infoBlocks={beneficiary.infoBlocks} />
      }
      topRightActions={
        searchBar && (
          <SearchAutoComplete
            className="w-96"
            placeholder={searchBar.placeholder}
            options={searchBar.options}
            search={search}
            setSearch={(value) => {
              setSearch(value);
              searchBar.onSearchChange?.(value);
            }}
            onSelect={searchBar.onSelect}
            isLoading={searchBar.isLoading}
            clearOnSelect
          />
        )
      }
      subHeader={subHeader}
    >
      {children}
    </StickyHeaderPage>
  );
}
